import { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { GenerateEmptyProgramWeeks } from 'functions/trainingTimelineUtils'
import moment from 'moment'

import TrainingTimelineSettings from 'components/Training/TrainingTimeline/TrainingTimelineSettings'
import TrainingTimelineViewport from 'components/Training/TrainingTimeline/TrainingTimelineViewport'
import TrainingTimelineTrack from 'components/Training/TrainingTimeline/TrainingTimelineTrack'
import MicroVolumeTimelineDisplay from 'components/Training/TrainingTimeline/MicroVolumeTimelineDisplay'
import MesoVolumeTimelineDisplay from 'components/Training/TrainingTimeline/MesoVolumeTimelineDisplay'
import MacroVolumeFocusTimelineDisplay from 'components/Training/TrainingTimeline/MacroVolumeFocusTimelineDisplay'
import MacroVolumePhaseTimelineDisplay from 'components/Training/TrainingTimeline/MacroVolumePhaseTimelineDisplay'
import ProgramLengthTimelineDisplay from 'components/Training/TrainingTimeline/ProgramLengthTimelineDisplay'
import MicroVolumeCompletionTimelineDisplay from 'components/Training/TrainingTimeline/MicroVolumeCompletionTimelineDisplay'
import MonthTimelineDisplay from 'components/Training/TrainingTimeline/MonthTimelineDisplay'

import useTimelineTrackVisibility from 'components/Training/TrainingTimeline/Helpers/useTimelineTrackVisibility'

const DISPLAY_RENDER_RANGE = false
const DAYS_PER_WEEK = 7
const ISO_WEEK_CENTER_DAY_OFFSET = 3 // ISO weeks run Mon–Sun; Thursday is the midpoint day (Mon + 3).
const BASE_DAY_WIDTH_PX = 5.6
const RENDER_BUFFER_DAYS = 14

export default function TrainingTimeline({
    clientDate,
    clientWeekOfTheYearKey,
    trainingTimelineStartDate,
    trainingTimelineEndDate,
    selectedWeekOfTheYearKey,
    setSelectedWeekOfTheYearKey = () => {},
    selectedWeekFocusKey = null, // optional. Changing it scrolls the track back to the selected week even if the selected week did not change
    defaultShowMicroVolume = true,
    defaultShowMesoVolume = true,
    defaultShowMacroFocus = true,
    defaultShowMacroPhase = true,
    defaultShowProgramLength = true,
    defaultShowMonth = true,
    trainingTimelinePrograms: trainingTimelineProgramsProp = [],
    kinesisEvents = [], // shown as flags on the program length bar
}) {

    const trainingTimelinePrograms = trainingTimelineProgramsProp ?? []

    const userType = useSelector(state => state.user.type)

    const displayDefaults = { defaultShowMicroVolume, defaultShowMesoVolume, defaultShowMacroFocus, defaultShowMacroPhase, defaultShowProgramLength, defaultShowMonth }
    const { showMicroVolume, showMesoVolume, showMacroFocus, showMacroPhase, showProgramLength, showMonth, displaySettingsOptions } = useTimelineTrackVisibility(displayDefaults)

    const viewportRef = useRef(null)
    const [viewportWidth, setViewportWidth] = useState(0)
    const [trainingTimelineItemWidthModifier, setTrainingTimelineItemWidthModifier] = useState(1.0)
    const dayWidth = useMemo(() => BASE_DAY_WIDTH_PX * trainingTimelineItemWidthModifier, [trainingTimelineItemWidthModifier])
    const viewportDayCount = useMemo(() => (dayWidth > 0 ? viewportWidth / dayWidth : 0), [viewportWidth, dayWidth])
    const viewportHalfDayCount = useMemo(() => viewportDayCount > 0 ? viewportDayCount / 2 : 0, [viewportDayCount])

    const animateTrackToWeekRef = useRef(() => {})
    const [trackCenteredWeekIndex, setTrackCenteredWeekIndex] = useState(0)
    const AnimateTrackToWeek = useCallback((weekIndex) => { animateTrackToWeekRef.current(weekIndex) }, [])

    // Track origin + where the camera is looking (shared by content layout)
    const presentMonday = useMemo(() => new WeekOfTheYearKey(clientWeekOfTheYearKey).toDate().startOf('day'), [clientWeekOfTheYearKey])
    const centerDay = trackCenteredWeekIndex * DAYS_PER_WEEK + ISO_WEEK_CENTER_DAY_OFFSET
    const dateToDayCenterX = useCallback((date) => date.diff(presentMonday, 'days') * dayWidth, [presentMonday, dayWidth])
    const dateToTrackLeftX = useCallback((date) => dateToDayCenterX(date) - dayWidth / 2, [dateToDayCenterX, dayWidth])
    const dateToWeekIndex = useCallback((date) => date.diff(presentMonday, 'days') / DAYS_PER_WEEK, [presentMonday])

    // Paint window — centered week +/- half the viewport, plus a small buffer
    const renderRangeStart = useMemo(
        () => presentMonday.clone().add(centerDay - viewportHalfDayCount - RENDER_BUFFER_DAYS, 'days'),
        [presentMonday, centerDay, viewportHalfDayCount],
    )

    const renderRangeEnd = useMemo(
        () => presentMonday.clone().add(centerDay + viewportHalfDayCount + RENDER_BUFFER_DAYS, 'days'),
        [presentMonday, centerDay, viewportHalfDayCount],
    )

    // Float the chevron above the selected week (populated micro or empty placeholder).
    const selectedMicroVolumeBar = useMemo(() => {
        if (dayWidth <= 0) {
            return null
        }

        // programVolumeInstance: flattenedProgramVolumeInstance,
        for (const trainingTimelineProgram of trainingTimelinePrograms) {
            for (const mesoVolumeConfig of trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []) {
                for (const microVolumeConfig of mesoVolumeConfig.microVolumes ?? []) {
                    if (microVolumeConfig.weekOfTheYearKey !== selectedWeekOfTheYearKey) continue

                    const startDate = moment(microVolumeConfig.startDate).startOf('day')
                    const endDate = moment(microVolumeConfig.endDate).startOf('day')
                    if (!startDate.isValid() || !endDate.isValid()) break

                    const left = dateToTrackLeftX(startDate)
                    const width = dateToTrackLeftX(endDate.clone().add(1, 'day')) - left
                    return { left, width }
                }
            }
        }

        const weekStart = new WeekOfTheYearKey(selectedWeekOfTheYearKey).toDate().startOf('day')
        const left = dateToTrackLeftX(weekStart)
        const width = dateToTrackLeftX(weekStart.clone().add(1, 'week')) - left
        return { left, width }
    }, [
        selectedWeekOfTheYearKey,
        trainingTimelinePrograms,
        dayWidth,
        dateToTrackLeftX,
    ])

    const HandleMicroVolumeClick = useCallback((microVolumeBar) => {
        const microVolumeConfigKey = microVolumeBar.microVolumeConfig.weekOfTheYearKey ?? microVolumeBar.key
        if (microVolumeConfigKey === selectedWeekOfTheYearKey) {
            AnimateTrackToWeek(Math.round(microVolumeBar.weekIndex))
            return
        }
        setSelectedWeekOfTheYearKey?.(microVolumeConfigKey)
    }, [selectedWeekOfTheYearKey, AnimateTrackToWeek, setSelectedWeekOfTheYearKey])

    const HandleEmptyWeekClick = useCallback((emptyWeekBar) => {
        if (emptyWeekBar.weekOfTheYearKey === selectedWeekOfTheYearKey) {
            AnimateTrackToWeek(Math.round(emptyWeekBar.weekIndex))
            return
        }
        setSelectedWeekOfTheYearKey?.(emptyWeekBar.weekOfTheYearKey)
    }, [selectedWeekOfTheYearKey, AnimateTrackToWeek, setSelectedWeekOfTheYearKey])

    useEffect(() => {
        if (!selectedWeekOfTheYearKey) return

        const weekStart = new WeekOfTheYearKey(selectedWeekOfTheYearKey).toDate().startOf('day')
        AnimateTrackToWeek(Math.round(dateToWeekIndex(weekStart)))
    }, [selectedWeekOfTheYearKey, selectedWeekFocusKey, dateToWeekIndex, AnimateTrackToWeek])

    const emptyWeekBars = useMemo(
        () => GenerateEmptyProgramWeeks(renderRangeStart, renderRangeEnd, dayWidth, dateToTrackLeftX, dateToWeekIndex),
        [renderRangeStart, renderRangeEnd, dayWidth, dateToTrackLeftX, dateToWeekIndex],
    )

    const renderRangeDebugStyle = useMemo(() => {
        if (!DISPLAY_RENDER_RANGE || dayWidth <= 0 || !renderRangeStart || !renderRangeEnd) {
            return null
        }

        const left = dateToTrackLeftX(renderRangeStart)
        const width = dateToTrackLeftX(renderRangeEnd.clone().add(1, 'day')) - left

        return { left: `${left}px`, width: `${width}px` }
    }, [dayWidth, renderRangeStart, renderRangeEnd, dateToTrackLeftX])

    return (
        <div className='w-full relative p-2'>
            {userType === 'super-admin' && (
                <TrainingTimelineSettings displaySettingsOptions={displaySettingsOptions} />
            )}

            <TrainingTimelineViewport
                viewportRef={viewportRef}
                setViewportWidth={setViewportWidth}
                setTrainingTimelineItemWidthModifier={setTrainingTimelineItemWidthModifier}
            >
                <TrainingTimelineTrack
                    viewportRef={viewportRef}
                    viewportWidth={viewportWidth}
                    dayWidth={dayWidth}
                    presentWeekOfTheYearKey={clientWeekOfTheYearKey}
                    timelineStartDate={trainingTimelineStartDate}
                    timelineEndDate={trainingTimelineEndDate}
                    onCenteredWeekIndexChange={setTrackCenteredWeekIndex}
                    animateTrackToWeekRef={animateTrackToWeekRef}
                >
                    {renderRangeDebugStyle && (
                        <div
                            className="absolute inset-y-0 z-0 pointer-events-none bg-sky-200/50"
                            style={renderRangeDebugStyle}
                        />
                    )}

                    {showMicroVolume && (
                        <div className="relative h-5 2xl:h-8 shrink-0">
                            {selectedMicroVolumeBar && (
                                <div
                                    className="absolute bottom-0 z-10 -translate-x-1/2 mb-1.5 pointer-events-none"
                                    style={{
                                        left: `${selectedMicroVolumeBar.left + selectedMicroVolumeBar.width / 2}px`,
                                    }}
                                >
                                    <ChevronDownIcon className="h-4 w-4 2xl:h-6 2xl:w-6 text-gray-600" />
                                </div>
                            )}
                        </div>
                    )}

                    <div className='relative flex flex-col shrink-0 gap-[3px] 2xl:gap-[4px]'>
                        {showMicroVolume && (
                            <div className="relative h-[75px] 2xl:h-[100px]">
                                {emptyWeekBars.map((week) => (
                                    <div
                                        key={week.key}
                                        className="absolute inset-y-0 flex flex-col items-center justify-end px-[1.5px] 2xl:px-[2px] cursor-pointer group"
                                        style={{
                                            left: `${week.left}px`,
                                            width: `${week.width}px`,
                                        }}
                                        onClick={() => HandleEmptyWeekClick(week)}
                                    >
                                        <div className="w-full rounded-sm 2xl:rounded-md bg-gray-300 h-2 transition-shadow" />
                                    </div>
                                ))}

                                {/* Each program's layer spans the whole row, so it lets clicks through: only its bars take them (otherwise it covers
                                    the empty weeks and the other programs' bars, and shows the viewport's drag cursor) */}
                                {trainingTimelinePrograms.map((trainingTimelineProgram, index) => (
                                    <div
                                        key={index}
                                        className="absolute inset-0 z-10 pointer-events-none"
                                    >
                                        <MicroVolumeTimelineDisplay
                                            macroVolumeFocusTypeHashMap={trainingTimelineProgram?.macroVolumeFocusTypeHashMap ?? {}}
                                            microVolumeStimulusTypeHashMap={trainingTimelineProgram?.microVolumeStimulusTypeHashMap ?? {}}
                                            mesoVolumes={trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []}
                                            renderRangeStart={renderRangeStart}
                                            renderRangeEnd={renderRangeEnd}
                                            daySpacing={dayWidth}
                                            DateToTrackLeftX={dateToTrackLeftX}
                                            DateToWeekIndex={dateToWeekIndex}
                                            HandleMicroVolumeClick={HandleMicroVolumeClick}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {showMesoVolume && (
                            <div className="relative h-4 2xl:h-5">
                                {trainingTimelinePrograms.map((trainingTimelineProgram, index) => (
                                    <div
                                        key={index}
                                        className="absolute inset-0"
                                    >
                                        <MesoVolumeTimelineDisplay
                                            renderRangeStart={renderRangeStart}
                                            renderRangeEnd={renderRangeEnd}
                                            daySpacing={dayWidth}
                                            programLevelHashMap={trainingTimelineProgram?.programLevelHashMap ?? {}}
                                            macroVolumeFocusTypeHashMap={trainingTimelineProgram?.macroVolumeFocusTypeHashMap ?? {}}
                                            mesoVolumes={trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []}
                                            DateToTrackLeftX={dateToTrackLeftX}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {showMacroFocus && (
                            <div className="relative h-4 2xl:h-5">
                                {trainingTimelinePrograms.map((trainingTimelineProgram, index) => (
                                    <div
                                        key={index}
                                        className="absolute inset-0"
                                    >
                                        <MacroVolumeFocusTimelineDisplay
                                            renderRangeStart={renderRangeStart}
                                            renderRangeEnd={renderRangeEnd}
                                            daySpacing={dayWidth}
                                            macroVolumeFocusTypeHashMap={trainingTimelineProgram?.macroVolumeFocusTypeHashMap ?? {}}
                                            macroVolumeFocuses={trainingTimelineProgram?.compiledProgramVolume?.macroVolumeFocuses ?? []}
                                            mesoVolumes={trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []}
                                            DateToTrackLeftX={dateToTrackLeftX}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {showMacroPhase && (
                            <div className="relative h-4 2xl:h-5">
                                {trainingTimelinePrograms.map((trainingTimelineProgram, index) => (
                                    <div
                                        key={index}
                                        className="absolute inset-0"
                                    >
                                        <MacroVolumePhaseTimelineDisplay
                                            renderRangeStart={renderRangeStart}
                                            renderRangeEnd={renderRangeEnd}
                                            daySpacing={dayWidth}
                                            macroVolumePhaseTypeHashMap={trainingTimelineProgram?.macroVolumePhaseTypeHashMap ?? {}}
                                            macroVolumePhases={trainingTimelineProgram?.compiledProgramVolume?.macroVolumePhases ?? []}
                                            mesoVolumes={trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []}
                                            DateToTrackLeftX={dateToTrackLeftX}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {showMicroVolume && (
                            <div className="relative h-4 2xl:h-5">
                                {trainingTimelinePrograms.map((trainingTimelineProgram, index) => (
                                    <div
                                        key={index}
                                        className="absolute inset-0 pointer-events-none" // Lets clicks through to the other programs' rings (each ring takes its own)
                                    >
                                        <MicroVolumeCompletionTimelineDisplay
                                            clientDate={clientDate}
                                            mesoVolumes={trainingTimelineProgram?.compiledProgramVolume?.mesoVolumes ?? []}
                                            renderRangeStart={renderRangeStart}
                                            renderRangeEnd={renderRangeEnd}
                                            daySpacing={dayWidth}
                                            DateToTrackLeftX={dateToTrackLeftX}
                                            DateToWeekIndex={dateToWeekIndex}
                                            HandleMicroVolumeClick={HandleMicroVolumeClick}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {showProgramLength && (
                            <ProgramLengthTimelineDisplay
                                trainingTimelinePrograms={trainingTimelinePrograms}
                                kinesisEvents={kinesisEvents}
                                allowProgramCanvas={userType === 'super-admin'}
                                renderRangeStart={renderRangeStart}
                                renderRangeEnd={renderRangeEnd}
                                daySpacing={dayWidth}
                                DateToTrackLeftX={dateToTrackLeftX}
                            />
                        )}

                        {showMonth && (
                            <MonthTimelineDisplay
                                renderRangeStart={renderRangeStart}
                                renderRangeEnd={renderRangeEnd}
                                DateToTrackLeftX={dateToTrackLeftX}
                            />
                        )}
                    </div>
                </TrainingTimelineTrack>
            </TrainingTimelineViewport>
        </div>
    )
}
